import React, { useRef, useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../store";
import { useTranslation } from "react-i18next";
import { fetchChatbotById } from "../../../features/ai_chatbots/aiChatbotsSlice";
import { FaCheck, FaCopy, FaCode, FaEye } from "react-icons/fa";
import { Icon } from "../../../components/common/Icon";
import AIChatbotFormModal from "./AIChatbotFormModal";

interface AIChatbotWidgetModalProps {
  show: boolean;
  onHide: () => void;
  chatbotId?: number | null;
}

const AIChatbotWidgetModal: React.FC<AIChatbotWidgetModalProps> = ({
  show,
  onHide,
  chatbotId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedChatbot, isLoading } = useSelector(
    (state: RootState) => state.aiChatbots
  );
  const [copied, setCopied] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (
      show &&
      chatbotId &&
      (!selectedChatbot || selectedChatbot.id !== chatbotId)
    ) {
      dispatch(fetchChatbotById(chatbotId));
    }
  }, [show, chatbotId, selectedChatbot, dispatch]);

  const handleCopy = () => {
    if (textAreaRef.current) {
      textAreaRef.current.select();
      document.execCommand("copy");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const generateWidgetCode = () => {
    if (!selectedChatbot) return "";

    const apiUrl =
      process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

    const widgetKey = selectedChatbot.widget_key;

    return `<!-- AI Chatbot Widget -->
<script src="${window.location.origin}/assets/js/chatbot-widget.js"></script>
<script>
  document.addEventListener('DOMContentLoaded', function() {
    AIChatbot.init({
      apiUrl: '${apiUrl}/public/chatbot',
      widgetKey: '${widgetKey}'
    });
  });
</script>`;
  };

  const ensureWidgetScript = () =>
    new Promise<void>((resolve, reject) => {
      if ((window as any).AIChatbot && typeof (window as any).AIChatbot.init === "function") {
        resolve();
        return;
      }
      const existing = document.querySelector('script[data-ai-widget="true"]') as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () => reject());
        return;
      }
      const s = document.createElement("script");
      s.src = `${window.location.origin}/assets/js/chatbot-widget.js`;
      s.async = true;
      (s as any).dataset.aiWidget = "true";
      s.onload = () => resolve();
      s.onerror = () => reject();
      document.body.appendChild(s);
    });

  const startPreview = async () => {
    if (!selectedChatbot) return;
    setIsPreviewing(true);
    const overlayId = "ai-chatbot-preview-overlay";
    const exitId = "ai-chatbot-exit-preview";
    const editId = "ai-chatbot-edit-settings";
    try {
      // Clean any previous preview DOM in case user previews another chatbot
      const prevOverlay = document.getElementById(overlayId);
      if (prevOverlay) prevOverlay.remove();
      const prevExit = document.getElementById(exitId);
      if (prevExit) prevExit.remove();
      const prevEdit = document.getElementById(editId);
      if (prevEdit) prevEdit.remove();
      const prevContainer = document.getElementById("ai-chatbot-widget-container");
      if (prevContainer) prevContainer.remove();

      // Create overlay that also handles click-to-close
      let overlay = document.getElementById(overlayId);
      if (!overlay) {
        overlay = document.createElement("div");
        overlay.id = overlayId;
        overlay.style.position = "fixed";
        (overlay.style as any).inset = "0";
        overlay.style.background = "rgba(15,23,42,0.35)";
        overlay.style.zIndex = "9998";
        (overlay.style as any).backdropFilter = "blur(2px)";
        overlay.style.pointerEvents = "auto";
        overlay.onclick = (e) => {
          if ((e.target as HTMLElement).id === overlayId) {
            stopPreview();
          }
        };
        document.body.appendChild(overlay);
      }

      // Exit preview button
      let exitBtn = document.getElementById(exitId) as HTMLButtonElement | null;
      if (!exitBtn) {
        exitBtn = document.createElement("button");
        exitBtn.id = exitId;
        exitBtn.textContent = "Exit Preview";
        exitBtn.style.position = "fixed";
        exitBtn.style.top = "16px";
        exitBtn.style.right = "16px";
        exitBtn.style.zIndex = "10000";
        exitBtn.style.padding = "10px 14px";
        exitBtn.style.borderRadius = "10px";
        exitBtn.style.border = "none";
        exitBtn.style.cursor = "pointer";
        exitBtn.style.fontWeight = "600";
        exitBtn.style.color = "#fff";
        exitBtn.style.background = "linear-gradient(135deg,#667eea,#764ba2)";
        exitBtn.onclick = () => stopPreview();
        document.body.appendChild(exitBtn);
      }

      // Edit settings button next to Exit
      let editBtn = document.getElementById(editId) as HTMLButtonElement | null;
      if (!editBtn) {
        editBtn = document.createElement("button");
        editBtn.id = editId;
        editBtn.textContent = "Edit";
        editBtn.style.position = "fixed";
        editBtn.style.top = "16px";
        editBtn.style.right = "140px";
        editBtn.style.zIndex = "10000";
        editBtn.style.padding = "10px 14px";
        editBtn.style.borderRadius = "10px";
        editBtn.style.border = "none";
        editBtn.style.cursor = "pointer";
        editBtn.style.fontWeight = "600";
        editBtn.style.color = "#fff";
        editBtn.style.background = "linear-gradient(135deg,#10b981,#22c55e)";
        editBtn.onclick = () => {
          stopPreview();
          setShowEditModal(true);
        };
        document.body.appendChild(editBtn);
      }

      await ensureWidgetScript();
      const apiUrl = (process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1") + "/public/chatbot";
      const widgetKey = selectedChatbot.widget_key;
      // Pass current widget_settings as an override so admin preview reflects unsaved changes if any
      let override: any = null;
      try {
        const ws = selectedChatbot.widget_settings;
        if (ws) override = typeof ws === 'string' ? JSON.parse(ws) : ws;
      } catch {}
      (window as any).AIChatbot.init({ apiUrl, widgetKey, widgetSettingsOverride: override });
    } catch (e) {
      console.error("Preview failed:", e);
    }
  };

  const stopPreview = () => {
    setIsPreviewing(false);
    const overlay = document.getElementById("ai-chatbot-preview-overlay");
    if (overlay) overlay.remove();
    const exitBtn = document.getElementById("ai-chatbot-exit-preview");
    if (exitBtn) exitBtn.remove();
    const editBtn = document.getElementById("ai-chatbot-edit-settings");
    if (editBtn) editBtn.remove();
    const container = document.getElementById("ai-chatbot-widget-container");
    if (container) container.remove();
  };

  if (!selectedChatbot || isLoading) {
    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("Widget Integration Code")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">{t("Loading chatbot data...")}</p>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t("Widget Integration Code")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3 d-flex align-items-center gap-2">
          <Icon icon={FaCode} className="me-2" />
          <h5 className="mb-0">{t("Code")}</h5>
        </div>
        <p>
          {t(
            "Add this script to your website to integrate the AI chatbot widget:"
          )}
        </p>
        <Form.Group>
          <Form.Control
            as="textarea"
            ref={textAreaRef}
            value={generateWidgetCode()}
            rows={8}
            readOnly
            className="font-monospace"
          />
        </Form.Group>
        {copied && (
          <Alert
            variant="success"
            className="mt-3 d-flex align-items-center"
          >
            <Icon icon={FaCheck} className="me-2" />
            {t("Code copied to clipboard!")}
          </Alert>
        )}
        <div className="mt-3">
          <h5>{t("Integration Instructions")}</h5>
          <ol className="ps-3">
            <li>{t("Copy the code above")}</li>
            <li>
              {t(
                "Paste it just before the closing </body> tag of your HTML"
              )}
            </li>
            <li>
              {t(
                "The chatbot widget will automatically appear in the selected position of your website"
              )}
            </li>
          </ol>
          <Alert variant="secondary" className="mt-3">
            <div className="mb-1"><strong>Tip:</strong> If your site uses a CSP, allow the widget script origin and API endpoint in script-src and connect-src.</div>
            <div className="mb-0"><strong>Note:</strong> The widget automatically pulls settings and initial message from your chatbot configuration.</div>
          </Alert>
        </div>
        {selectedChatbot.allowed_domains && selectedChatbot.allowed_domains.length > 0 && (
          <Alert variant="info" className="mt-3">
            <strong>{t("Domain Restriction")}</strong>: {t("This widget will only work on the following domains:")}
            <ul className="mb-0 mt-1">
              {selectedChatbot.allowed_domains.map((domain: string, index: number) => (
                <li key={index}>{domain}</li>
              ))}
            </ul>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("Close")}
        </Button>
        <Button variant="outline-primary" onClick={startPreview} disabled={isPreviewing}>
          <Icon icon={FaEye} className="me-2" /> {isPreviewing ? t("Previewing...") : t("Preview")}
        </Button>
        <Button variant="primary" onClick={handleCopy}>
          <Icon icon={FaCopy} className="me-2" /> {t("Copy Code")}
        </Button>
      </Modal.Footer>
      {showEditModal && (
        <AIChatbotFormModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          chatbot={selectedChatbot}
          onSuccess={() => setShowEditModal(false)}
          initialActiveTab="widget-settings"
        />
      )}
    </Modal>
  );
};

export default AIChatbotWidgetModal;
